// utils/charsRemaining.ts

/**
 * Calculates and updates the number of remaining characters allowed in an input field,
 * taking into account the length of an optional modifier element (e.g., a <select> or another input).
 * If the total character count exceeds the max limit, the input is truncated accordingly.
 *
 * @param {HTMLInputElement} inputElem - The input element being monitored for character count.
 * @param {HTMLElement} charsRemainingElem - The container element displaying remaining characters.
 * @param {HTMLElement} counterElem - The specific element where the remaining character count is shown.
 * @param {HTMLSelectElement | HTMLInputElement | null} modifierElem - Optional modifier element whose content affects the total character count.
 * @param {number} maxLimit - Maximum allowed number of characters (input + modifier).
 */
export function countCharsRemaining(
	inputElem: HTMLInputElement,
	charsRemainingElem: HTMLElement,
	counterElem: HTMLElement,
	modifierElem: HTMLSelectElement | HTMLInputElement | null,
	maxLimit: number,
): void {
	const charsLength = inputElem.value.length;
	let modifierAmount = 0,
		charsRemaining = 0,
		totalChars = 0,
		charsToDeduct = 0,
		delimiterLength = 3; // `delimiterLength` helps calculate offsets based on the fixed delimiter length - " - ".

	if (modifierElem instanceof HTMLSelectElement) {
		const modifierVal = modifierElem.options[modifierElem.selectedIndex].text;
		modifierAmount = modifierVal.length + delimiterLength;
	} else if (modifierElem instanceof HTMLInputElement) {
		const modifierVal = modifierElem.value;
		modifierAmount = modifierVal.length + delimiterLength;
	}

	totalChars = charsLength + modifierAmount;
	charsRemaining = maxLimit - charsLength - modifierAmount;

	if (maxLimit <= totalChars) {
		charsToDeduct = totalChars - maxLimit;
		const length = inputElem.value.length;
		const chars = Math.min(charsToDeduct, length);
		inputElem.value = inputElem.value.slice(0, length - chars);
		counterElem.innerHTML = '0'; // forcing this
		charsRemainingElem.classList.add('max-chars');
	} else {
		counterElem.innerHTML = charsRemaining.toString();
		charsRemainingElem.classList.remove('max-chars');
	}
}
